import React, { useState } from 'react';
import DynamicForm from '../DynamicForm';
import { sharedStyles } from '../../shared/styles';
import makeStyles from '@mui/styles/makeStyles';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Recycle from '@a2d24/care-at-work-core/Recycle';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../ConfirmDialog';
import { FacialVerificationDialog } from '../FacialIdVerification/FacialVerificationDialog';
import { createProcedureVerificationTask, getPresignedUrlForProcedureVerificationUpload } from '../../Apis/FacialRecognition';
import useDifferedTask from '@a2d24/differed-task/react/useDifferedTask';
import { useStores } from '@a2d24/care-at-work-state/stores';
import { WorkflowType } from '../FacialIdVerification/utils';

const ProcedureDetails = ({
    config,
    props: { procedure, consult_id, consult, procedure_values },
    refresh,
}) => {
    const facialRecognitionConsult = consult.facial_recognition_required;
    const { authStore } = useStores();
    const navigate = useNavigate();
    const classes = useStyles();
    const sharedClasses = sharedStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [showDialog, setShowDialog] = useState(false);
    const facialRecognitionRequired = facialRecognitionConsult && procedure?.requires_facial_recognition;
    const [facialRecognitionDialogOpen, setFacialRecognitionDialogOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [facialRecognitionTaskId, setFacialRecognitionTaskId] = useState(null);

    const differedFacialRecognitionData = useDifferedTask(facialRecognitionTaskId, {
        websocketUrl: process.env.REACT_APP_WS_ENDPOINT,
        token: authStore.occhealthToken,
    });

    const openFacialRecognitionDialog = () => {
        createProcedureVerificationTask(
            consult.patient.primary_identifier.value,
            consult_id,
            procedure.id
        ).then((res) => {
            setFacialRecognitionTaskId(res.toString());
            setFacialRecognitionDialogOpen(true);
        }).catch((e)=>{
            enqueueSnackbar(e, { variant: 'error' });
        });
    };

    const oncFacialRecognitionDialogClose = () => {
        setPendingValues(null);
        setFacialRecognitionDialogOpen(false);
    };

    const getFacialRecognitionPostUrl = (file) => {
        return getPresignedUrlForProcedureVerificationUpload(
            consult_id,
            procedure.id,
            facialRecognitionTaskId,
            consult.patient.primary_identifier.value,
            file.type,
            file.extension
        );
    };

    const restartFacialRecognition = async () => {
        createProcedureVerificationTask(
            consult.patient.primary_identifier.value,
            consult_id,
            procedure.id
        ).then((res) => {
            setFacialRecognitionTaskId(res.toString());
        }).catch((e)=>{
            enqueueSnackbar(e, { variant: 'error' });
        });
    };

    const onSubmit = async (values) => {
        if (facialRecognitionRequired) {
            setPendingValues(values);
            openFacialRecognitionDialog();
        } else {
            await saveProcedure(values);
        }
    };

    const saveProcedure = async (values) => {
        const version = procedure_values.version || 0;
        const draft_version = procedure_values.draft_version || 0;

        await Recycle.rpcPostV2({
            controller: 'ProcedureController',
            method: 'save_procedure_details',
            kwargs: {
                consult_id,
                procedure_id: procedure.id,
                values,
                version,
                draft_version,
                procedure_name: procedure.name,
            },
        })
            .then((res) => {
                enqueueSnackbar(`Saved ${procedure.name}`, { variant: 'success' });
                navigate(`/consults/${consult_id}`);
            })
            .catch((err) => {
                if (err.response?.status === 409) {
                    setShowDialog(true);
                    return;
                }
                enqueueSnackbar(`Failed to save  ${procedure.name}!`, { variant: 'error' });
                if (err.response?.data?.message) {
                    enqueueSnackbar(err.response?.data?.message, { variant: 'error' });
                }
            });
    };

    const onSaveDraft = async (values) => {
        const version = procedure_values.draft_version || 0;

        await Recycle.rpcPostV2({
            controller: 'ProcedureController',
            method: 'save_procedure_details_draft',
            kwargs: {
                consult_id,
                procedure_id: procedure.id,
                values,
                version,
                procedure_name: procedure.name,
            },
        })
            .then((res) => {
                enqueueSnackbar(`Saved ${procedure.name} draft`, { variant: 'success' });
                navigate(`/consults/${consult_id}`);
            })
            .catch((err) => {
                if (err.response?.status === 409) {
                    setShowDialog(true);
                    return;
                }
                enqueueSnackbar(`Failed to save  ${procedure.name} draft!`, { variant: 'error' });
                if (err.response?.data?.message) {
                    enqueueSnackbar(err.response?.data?.message, { variant: 'error' });
                }
            });
    };

    const onCloseDialog = () => {
        setShowDialog(false);
        refresh();
    };

    const proceedWithSave = async () => {
        if (pendingValues) {
            await saveProcedure(pendingValues);
            setPendingValues(null);
        }
    };

    return (
        <React.Fragment>
            <Paper>
                <div className={classes.container}>
                    <div className={sharedClasses.heading}>{procedure.name}</div>
                    <Divider className={classes.divider} variant="middle" />
                    <DynamicForm
                        onSubmit={onSubmit}
                        config={{
                            fields: procedure,
                            defaultValues: procedure_values.values || {},
                        }}
                        extraValues={{ patient: consult.patient }}
                        actions={[
                            ({ submitting }) => (
                                <Button
                                    key="cancel"
                                    disabled={submitting}
                                    onClick={() => navigate(`/consults/${consult_id}`)}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                            ),
                            ({ submitting, getValues }) =>
                                procedure_values.is_draft !== false ? (
                                    <Button
                                        key="save_draft"
                                        variant="contained"
                                        disabled={submitting}
                                        onClick={() => onSaveDraft(getValues())}
                                    >
                                        Save draft
                                    </Button>
                                ) : null,
                            ({ submitting }) => (
                                <Button
                                    key="save"
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={submitting}
                                >
                                    Save
                                </Button>
                            ),
                        ]}
                    />
                </div>
            </Paper>

            <ConfirmDialog
                open={showDialog}
                title="Note"
                message="Your procedure form data is outdated, this may be due to another clinician capturing data, the page will now refresh"
                onClose={onCloseDialog}
            />

            {facialRecognitionRequired ? (
                <FacialVerificationDialog
                    open={facialRecognitionDialogOpen}
                    onClose={oncFacialRecognitionDialogClose}
                    task_id={facialRecognitionTaskId}
                    patient={consult.patient}
                    workflowType={WorkflowType.PROCEDURE_VERIFICATION}
                    verificationStatus={differedFacialRecognitionData?.progress?.state}
                    imageUrl={differedFacialRecognitionData?.progress?.s3_url}
                    getFacialRecognitionPresignedUrl={getFacialRecognitionPostUrl}
                    restartVerification={restartFacialRecognition}
                    onProceed={proceedWithSave}
                />
            ) : null}
        </React.Fragment>
    );
};

const useStyles = makeStyles((theme) => ({
    container: {
        padding: 15,
    },
    heading: {
        color: theme.palette.primary.main,
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: theme.spacing(3),
    },
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

export default ProcedureDetails;
